import React from 'react'

import cn from '../../../../commons/src/libs/class-names'
import {
  countChildrenWithFragments,
  mapChildrenWithFragments,
} from '../../../../commons/src/services/react-children'

import {CommonProps, Overwrite} from '../../../../commons/src/types'

import TableCell from './TableCell'

type Props = Overwrite<
  CommonProps<'div'>,
  {
    children: React.ReactNode
    computedWidths?: string[]
    aligns?: ('left' | 'center' | 'right')[]
    subRows?: React.ReactNode
    emphasized?: boolean
    onClick?: () => void
  }
>

const TableRow = ({
  className,
  children,
  computedWidths = [],
  aligns = [],
  subRows = [],
  emphasized = false,
  onClick,
  ...props
}: Props) => {
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      const target = event.target as Element
      // only handle clicks on free space within the row, so users can for example copy content by double click
      if (target.matches('.TableCell') || target.matches('.TableCell__content')) {
        onClick()
      }
    }
  }

  const childCount = countChildrenWithFragments(children)

  return (
    <div
      {...props}
      className={cn('TableRow', {clickable: !!onClick, emphasized}, className)}
      onClick={handleClick}
    >
      <div className="TableRow__content">
        {mapChildrenWithFragments<React.ComponentProps<typeof TableCell>>(children, (child, i) =>
          React.cloneElement(child, {
            computedWidth: computedWidths[i],
            align: aligns[i],
            firstCell: i === 0,
            lastCell: i === childCount - 1,
          })
        )}
      </div>
      <div className="TableRow__subs">
        {mapChildrenWithFragments<React.ComponentProps<typeof TableRow | typeof TableCell>>(
          subRows,
          (child, i) =>
            React.cloneElement(child, {
              computedWidths,
              aligns,
              key: i,
              subRow: true,
            })
        )}
      </div>
    </div>
  )
}

export default TableRow
